import { jsPDF } from 'jspdf';

// ─── Brand colours ────────────────────────────────────────────────────────────
const NAVY   = [26,  62,  95];
const TEAL   = [0,   95, 115];
const ACCENT = [10, 147, 150];
const TEXT   = [30,  41,  59];
const MUTED  = [100, 116, 139];
const WHITE  = [255, 255, 255];
const RULE   = [203, 213, 225];

// ─── Layout ───────────────────────────────────────────────────────────────────
const PAGE_W    = 210;
const PAGE_H    = 297;
const MARGIN    = 18;
const CONTENT_W = PAGE_W - MARGIN * 2;
const BOTTOM    = PAGE_H - 16;
const HEADER_H  = 38;   // full header height (page 1)
const CONT_TOP  = 12;   // continuation pages top margin

// ─── Helpers ──────────────────────────────────────────────────────────────────
function setFill(doc, arr)  { doc.setFillColor(arr[0], arr[1], arr[2]); }
function setTxt(doc, arr)   { doc.setTextColor(arr[0], arr[1], arr[2]); }
function resetTxt(doc)      { setTxt(doc, TEXT); }

/** Replace characters unsupported by jsPDF's built-in Helvetica with ASCII equivalents */
function sanitize(str) {
  if (!str) return '';
  return str
    .replace(/\u2014|\u2013/g, '-')        // em/en dash
    .replace(/\u2019|\u2018/g, "'")        // smart single quotes
    .replace(/\u201C|\u201D/g, '"')        // smart double quotes
    .replace(/\u2192|\u2190|\u2794/g, '->') // arrows
    .replace(/\u2022/g, '-')               // bullet
    .replace(/\u00A0/g, ' ')               // non-breaking space
    .replace(/[^\x00-\x7E]/g, '');         // strip remaining non-ASCII
}

function drawPageOneHeader(doc, title, subtitle) {
  setFill(doc, NAVY);
  doc.rect(0, 0, PAGE_W, HEADER_H - 2, 'F');
  setFill(doc, ACCENT);
  doc.rect(0, HEADER_H - 2, PAGE_W, 2, 'F');

  setTxt(doc, WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(sanitize(title), MARGIN, 16);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(200, 220, 235);
  doc.text(sanitize(subtitle), MARGIN, 27);

  const dateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  doc.text(`Generated ${dateStr}`, PAGE_W - MARGIN, 27, { align: 'right' });
  resetTxt(doc);
}

function drawContinuationTop(doc) {
  // Thin accent strip only — no repeated title
  setFill(doc, NAVY);
  doc.rect(0, 0, PAGE_W, 4, 'F');
  setFill(doc, ACCENT);
  doc.rect(0, 4, PAGE_W, 1.5, 'F');
}

function drawFooter(doc, pageNum, totalPages) {
  const y = PAGE_H - 9;
  setFill(doc, RULE);
  doc.rect(MARGIN, y - 2.5, CONTENT_W, 0.3, 'F');
  setTxt(doc, MUTED);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.text('Rare Impact Framework  |  Resilience Forecasting Summary', MARGIN, y);
  doc.text(`Page ${pageNum} of ${totalPages}`, PAGE_W - MARGIN, y, { align: 'right' });
  resetTxt(doc);
}

/** Add a continuation page and return the reset y cursor */
function addPage(doc) {
  doc.addPage();
  drawContinuationTop(doc);
  return CONT_TOP + 8;
}

/** If adding `needed` mm would overflow, start a new page */
function guard(doc, y, needed) {
  if (y + needed > BOTTOM) return addPage(doc);
  return y;
}

// ─── Score cards ──────────────────────────────────────────────────────────────
function drawScoreCards(doc, scores, startY) {
  const cardW = (CONTENT_W - 8) / 3;
  const cardH = 22;
  const labels   = ['CURRENT', '2030 GOAL', '2035 GOAL'];
  const values   = [scores.current, scores.goal2030, scores.goal2035];
  const colors   = [[180, 83, 9], [14, 116, 144], [21, 128, 61]];
  const bgColors = [[255, 251, 235], [236, 254, 255], [240, 253, 244]];

  labels.forEach((label, i) => {
    const x = MARGIN + i * (cardW + 4);
    setFill(doc, bgColors[i]);
    doc.roundedRect(x, startY, cardW, cardH, 3, 3, 'F');
    setFill(doc, RULE);
    doc.roundedRect(x, startY, cardW, cardH, 3, 3, 'S');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(colors[i][0], colors[i][1], colors[i][2]);
    doc.text(label, x + cardW / 2, startY + 5.5, { align: 'center' });

    doc.setFontSize(14);
    const val = values[i];
    doc.text(val != null ? `${Number(val).toFixed(2)}/4` : 'Not set', x + cardW / 2, startY + 13, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(val != null ? `${((val / 4) * 100).toFixed(0)}% resilience` : '', x + cardW / 2, startY + 19, { align: 'center' });
  });

  resetTxt(doc);
  return startY + cardH + 5;
}

// ─── Country table ────────────────────────────────────────────────────────────
function drawCountryTable(doc, countryData, startY) {
  let y = startY;
  const cols    = [58, 18, 24, 24, 24, 26];
  const headers = ['Country', 'Sites', 'Current', '2030 Goal', '2035 Goal', 'Progress'];
  const rowH    = 5.5;

  // Section heading
  y = guard(doc, y, 8 + rowH);
  setFill(doc, TEAL);
  doc.rect(MARGIN, y, CONTENT_W, 7, 'F');
  setTxt(doc, WHITE);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9.5);
  doc.text('Resilience Scores by Country', MARGIN + 3, y + 5);
  y += 9;

  // Column header row
  setFill(doc, [241, 245, 249]);
  doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  setTxt(doc, MUTED);
  let xPos = MARGIN + 2;
  headers.forEach((h, i) => {
    doc.text(h, i === 0 ? xPos : xPos + cols[i] / 2, y + rowH - 1.2, { align: i === 0 ? 'left' : 'center' });
    xPos += cols[i];
  });
  y += rowH;

  // Data rows
  countryData.forEach((country, idx) => {
    y = guard(doc, y, rowH);
    if (idx % 2 === 1) {
      setFill(doc, [248, 250, 252]);
      doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
    }
    setFill(doc, RULE);
    doc.rect(MARGIN, y + rowH - 0.2, CONTENT_W, 0.2, 'F');

    xPos = MARGIN + 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    setTxt(doc, TEXT);
    doc.text(sanitize(country.countryName || ''), xPos, y + rowH - 1.2);
    xPos += cols[0];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    setTxt(doc, MUTED);
    doc.text(String(country.siteCount ?? '-'), xPos + cols[1] / 2, y + rowH - 1.2, { align: 'center' });
    xPos += cols[1];

    const fmt = (v) => v != null ? Number(v).toFixed(2) : '-';
    setTxt(doc, TEXT);
    [country.avgCurrent, country.avg5yrs, country.avg10yrs].forEach((v, i) => {
      doc.text(fmt(v), xPos + cols[2 + i] / 2, y + rowH - 1.2, { align: 'center' });
      xPos += cols[2 + i];
    });

    const rawDelta = country.avg10yrs != null && country.avgCurrent != null
      ? country.avg10yrs - country.avgCurrent : null;
    const deltaStr = rawDelta != null
      ? (rawDelta >= 0 ? `+${rawDelta.toFixed(2)}` : rawDelta.toFixed(2)) : '-';
    doc.setTextColor(
      rawDelta != null && rawDelta > 0 ? 21  : 100,
      rawDelta != null && rawDelta > 0 ? 128 : 116,
      rawDelta != null && rawDelta > 0 ? 61  : 139
    );
    doc.text(deltaStr, xPos + cols[5] / 2, y + rowH - 1.2, { align: 'center' });
    resetTxt(doc);
    y += rowH;
  });

  return y + 5;
}

// ─── Body text renderer ───────────────────────────────────────────────────────
function renderBody(doc, lines, startY, maxW) {
  let y = startY;
  const lineH   = 5.0;
  const paraGap = 2.5;

  // Clean a line: strip ALL leading hashes, bold/italic markers
  const clean = (s) => sanitize(
    s.replace(/^#{1,6}\s+/, '')
     .replace(/\*\*(.+?)\*\*/g, '$1')
     .replace(/\*(.+?)\*/g, '$1')
     .replace(/^[-]\s+/, '- ')
  );

  lines.forEach((rawLine) => {
    const trimmed = rawLine.trim();

    // Blank line
    if (!trimmed) { y += paraGap; return; }

    // Heading levels — match 1-6 hashes
    const hMatch = trimmed.match(/^(#{1,6})\s+(.+)$/);
    const isH1H2 = hMatch && hMatch[1].length <= 2;
    const isH3H4 = hMatch && hMatch[1].length >= 3;
    // Standalone bold line (entire line is **...**)
    const isBoldLine = !hMatch && /^\*\*[^*]+\*\*:?\s*$/.test(trimmed);
    // Bullet
    const isBullet = /^[-*]\s/.test(trimmed) || /^•/.test(trimmed);
    // Numbered list
    const isNumbered = /^\d+\.\s/.test(trimmed);

    const text = hMatch ? sanitize(hMatch[2]) : clean(rawLine);

    if (isH1H2) {
      y = guard(doc, y, 12);
      y += 2;
      setFill(doc, TEAL);
      doc.rect(MARGIN, y - 1, CONTENT_W, 7, 'F');
      setTxt(doc, WHITE);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const wrapped = doc.splitTextToSize(text, CONTENT_W - 6);
      doc.text(wrapped, MARGIN + 3, y + 4.5);
      y += 9;
      resetTxt(doc);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);

    } else if (isH3H4) {
      const wrapped = doc.splitTextToSize(text, maxW);
      y = guard(doc, y, wrapped.length * lineH + 5);
      y += 2;
      setTxt(doc, TEAL);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.text(wrapped, MARGIN, y);
      y += wrapped.length * lineH + 1;
      resetTxt(doc);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);

    } else if (isBoldLine) {
      const wrapped = doc.splitTextToSize(text, maxW);
      y = guard(doc, y, wrapped.length * lineH + 3);
      y += 1.5;
      setTxt(doc, TEAL);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.text(wrapped, MARGIN, y);
      y += wrapped.length * lineH + 0.5;
      resetTxt(doc);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);

    } else if (isBullet) {
      const bulletText = `- ${text.replace(/^[-*•]\s*/, '')}`;
      const wrapped = doc.splitTextToSize(bulletText, maxW - 4);
      y = guard(doc, y, wrapped.length * lineH);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      resetTxt(doc);
      wrapped.forEach((l, i) => {
        doc.text(l, MARGIN + (i === 0 ? 0 : 4), y);
        y += lineH;
      });

    } else if (isNumbered) {
      // Preserve number prefix, wrap remainder
      const numMatch = trimmed.match(/^(\d+\.)\s+(.+)$/);
      const prefix  = numMatch ? numMatch[1] : '';
      const rest    = numMatch ? sanitize(numMatch[2].replace(/\*\*(.+?)\*\*/g, '$1').replace(/\*(.+?)\*/g, '$1')) : text;
      const fullLine = `${prefix} ${rest}`;
      const wrapped  = doc.splitTextToSize(fullLine, maxW - 4);
      y = guard(doc, y, wrapped.length * lineH);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      resetTxt(doc);
      wrapped.forEach((l, i) => {
        doc.text(l, MARGIN + (i === 0 ? 0 : 6), y);
        y += lineH;
      });

    } else {
      const wrapped = doc.splitTextToSize(text, maxW);
      y = guard(doc, y, wrapped.length * lineH);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      resetTxt(doc);
      doc.text(wrapped, MARGIN, y);
      y += wrapped.length * lineH;
    }
  });

  return y;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export function downloadNationalSummaryPDF(summary) {
  try {
    const doc      = new jsPDF({ unit: 'mm', format: 'a4' });
    const country  = summary.countryName || 'Unknown';
    const title    = `National Impact Summary - ${country}`;
    const subtitle = `${summary.siteCount ?? 0} managed access sites  |  Rare Impact Framework`;

    drawPageOneHeader(doc, title, subtitle);
    let y = HEADER_H + 6;

    if (summary.overallScores) {
      y = drawScoreCards(doc, summary.overallScores, y);
    }

    if (summary.summary) {
      y = renderBody(doc, summary.summary.split('\n'), y, CONTENT_W);
    }

    const total = doc.getNumberOfPages();
    for (let p = 1; p <= total; p++) { doc.setPage(p); drawFooter(doc, p, total); }

    doc.save(`national_summary_${country.replace(/\s+/g, '_').toLowerCase()}_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (err) {
    console.error('[summaryPdf] downloadNationalSummaryPDF failed:', err);
    alert(`PDF generation failed: ${err.message}`);
  }
}

export function downloadGlobalSummaryPDF(summary) {
  try {
    const doc      = new jsPDF({ unit: 'mm', format: 'a4' });
    const title    = 'Global Impact Summary';
    const subtitle = `${summary.countryCount ?? 0} countries  |  ${summary.siteCount ?? 0} managed access sites  |  Rare Impact Framework`;

    drawPageOneHeader(doc, title, subtitle);
    let y = HEADER_H + 6;

    if (summary.overallScores) {
      y = drawScoreCards(doc, summary.overallScores, y);
    }

    if (summary.countryData && summary.countryData.length > 0) {
      y = drawCountryTable(doc, summary.countryData, y);
    }

    if (summary.summary) {
      y = guard(doc, y, 20);
      y = renderBody(doc, summary.summary.split('\n'), y, CONTENT_W);
    }

    const total = doc.getNumberOfPages();
    for (let p = 1; p <= total; p++) { doc.setPage(p); drawFooter(doc, p, total); }

    doc.save(`global_summary_${new Date().toISOString().split('T')[0]}.pdf`);
  } catch (err) {
    console.error('[summaryPdf] downloadGlobalSummaryPDF failed:', err);
    alert(`PDF generation failed: ${err.message}`);
  }
}
